import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import Swal from 'sweetalert2';
import { NotificationService } from '../../../../../services/notifications';

@Component({
  selector: 'app-notificationsmobile',
  templateUrl: './notificationsmobile.component.html',
  styleUrl: './notificationsmobile.component.scss'
})
export class NotificationsmobileComponent implements OnInit {
  desktopPreview: string | null = null;
  mobilePreview: string | null = null;

  allData: any[] = [];
  visibleNotifications: any[] = [];

  contentReady = true;
  noData = false;
  isLoadingMore = false;

  pageSize = 8;

  isAr = typeof localStorage !== 'undefined'
    ? localStorage.getItem('language') === 'ar'
    : false;

  notificationForm!: FormGroup;

  selectedImage: File | null = null;
  selectedMobileImage: File | null = null;

  constructor(
    private notificationService: NotificationService,
    private fb: FormBuilder,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadNotifications();
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (
      this.contentReady ||
      this.noData ||
      this.isLoadingMore ||
      !this.hasMoreNotifications
    ) {
      return;
    }

    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    const bottomReached = scrollPosition + windowHeight >= pageHeight - 120;

    if (bottomReached) {
      this.loadMore();
    }
  }

  get hasMoreNotifications(): boolean {
    return this.visibleNotifications.length < this.allData.length;
  }

  initForm(): void {
    this.notificationForm = this.fb.group({
      notification_head_ar: ['', Validators.required],
      notification_head_en: ['', Validators.required],
      notification_body_ar: [''],
      notification_body_en: [''],
      link: [''],
      image: [null],
      mobile_image: [null]
    });
  }

  loadNotifications(): void {
    this.contentReady = true;

    this.notificationService.getAllNotifications().subscribe({
      next: (res: any) => {
        this.allData = this.normalizeResponse(res);
        this.visibleNotifications = [];
        this.noData = this.allData.length === 0;
        this.contentReady = false;

        this.loadMore();
      },
      error: (err) => {
        console.error(err);
        this.contentReady = false;
        this.noData = true;

        Swal.fire({
          icon: 'error',
          title: this.t('notificationMobile.alerts.error.title'),
          text: this.t('notificationMobile.alerts.error.loadFailed'),
          confirmButtonText: this.t('notificationMobile.alerts.ok')
        });
      }
    });
  }

  normalizeResponse(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }

    if (Array.isArray(res?.data)) {
      return res.data;
    }

    if (Array.isArray(res?.notifications)) {
      return res.notifications;
    }

    return [];
  }

  loadMore(): void {
    if (!this.hasMoreNotifications || this.isLoadingMore) {
      return;
    }

    this.isLoadingMore = true;

    const currentLength = this.visibleNotifications.length;
    const nextItems = this.allData.slice(currentLength, currentLength + this.pageSize);

    this.visibleNotifications = [...this.visibleNotifications, ...nextItems];

    setTimeout(() => {
      this.isLoadingMore = false;
    }, 150);
  }

  onFileSelected(event: Event): void {
    this.handleImageSelection(event, 'desktop');
  }

  onMobileImageSelected(event: Event): void {
    this.handleImageSelection(event, 'mobile');
  }

  handleImageSelection(event: Event, type: 'desktop' | 'mobile'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const maxSize = 2 * 1024 * 1024;

    if (file.size > maxSize) {
      Swal.fire({
        icon: 'error',
        title: this.t('notificationMobile.alerts.imageTooLarge.title'),
        text: this.t('notificationMobile.alerts.imageTooLarge.text'),
        confirmButtonText: this.t('notificationMobile.alerts.ok')
      });

      input.value = '';

      if (type === 'desktop') {
        this.selectedImage = null;
        this.desktopPreview = null;
        this.notificationForm.patchValue({ image: null });
      } else {
        this.selectedMobileImage = null;
        this.mobilePreview = null;
        this.notificationForm.patchValue({ mobile_image: null });
      }

      return;
    }

    if (type === 'desktop') {
      this.selectedImage = file;
      this.notificationForm.patchValue({ image: file });
    } else {
      this.selectedMobileImage = file;
      this.notificationForm.patchValue({ mobile_image: file });
    }

    const reader = new FileReader();

    reader.onload = () => {
      if (type === 'desktop') {
        this.desktopPreview = reader.result as string;
      } else {
        this.mobilePreview = reader.result as string;
      }
    };

    reader.readAsDataURL(file);
  }

  addNotification(): void {
    if (this.notificationForm.invalid) {
      this.notificationForm.markAllAsTouched();
      return;
    }

    const formData = new FormData();

    formData.append(
      'notification_head_ar',
      this.notificationForm.value.notification_head_ar
    );

    formData.append(
      'notification_head_en',
      this.notificationForm.value.notification_head_en
    );

    formData.append(
      'notification_body_ar',
      this.notificationForm.value.notification_body_ar || ''
    );

    formData.append(
      'notification_body_en',
      this.notificationForm.value.notification_body_en || ''
    );

    formData.append('link', this.notificationForm.value.link || '');

    if (this.selectedImage) {
      formData.append('image', this.selectedImage);
    }

    if (this.selectedMobileImage) {
      formData.append('mobile_image', this.selectedMobileImage);
    }

    this.contentReady = true;

    this.notificationService.addNotification(formData).subscribe({
      next: () => {
        this.resetForm();
        this.loadNotifications();

        Swal.fire({
          icon: 'success',
          title: this.t('notificationMobile.alerts.saved.title'),
          text: this.t('notificationMobile.alerts.saved.text'),
          confirmButtonText: this.t('notificationMobile.alerts.ok')
        });
      },
      error: (err) => {
        console.error('Error adding notification:', err);

        this.contentReady = false;

        const backendMessage =
          err?.error?.errors?.image?.[0] ||
          err?.error?.errors?.mobile_image?.[0] ||
          err?.error?.message ||
          this.t('notificationMobile.alerts.error.operationFailed');

        Swal.fire({
          icon: 'error',
          title: this.t('notificationMobile.alerts.error.title'),
          text: this.translateBackendError(backendMessage),
          confirmButtonText: this.t('notificationMobile.alerts.ok')
        });
      }
    });
  }

  resetForm(): void {
    this.notificationForm.reset();

    this.selectedImage = null;
    this.selectedMobileImage = null;

    this.desktopPreview = null;
    this.mobilePreview = null;
  }

  deleteItem(id: number): void {
    Swal.fire({
      title: this.t('notificationMobile.alerts.deleteConfirm.title'),
      text: this.t('notificationMobile.alerts.deleteConfirm.text'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.t('notificationMobile.alerts.deleteConfirm.confirm'),
      cancelButtonText: this.t('notificationMobile.alerts.deleteConfirm.cancel')
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.contentReady = true;

      this.notificationService.deleteNotification(id).subscribe({
        next: () => {
          this.loadNotifications();

          Swal.fire({
            icon: 'success',
            title: this.t('notificationMobile.alerts.deleted.title'),
            text: this.t('notificationMobile.alerts.deleted.text'),
            confirmButtonText: this.t('notificationMobile.alerts.ok')
          });
        },
        error: (err) => {
          console.error(err);

          this.contentReady = false;

          Swal.fire({
            icon: 'error',
            title: this.t('notificationMobile.alerts.error.title'),
            text: this.t('notificationMobile.alerts.error.deleteFailed'),
            confirmButtonText: this.t('notificationMobile.alerts.ok')
          });
        }
      });
    });
  }

  translateBackendError(message: string): string {
    if (!message) {
      return this.t('notificationMobile.alerts.error.unexpected');
    }

    if (message.includes('The image field must not be greater than 2048 kilobytes')) {
      return this.t('notificationMobile.alerts.imageTooLarge.desktopText');
    }

    if (message.includes('The mobile image field must not be greater than 2048 kilobytes')) {
      return this.t('notificationMobile.alerts.imageTooLarge.mobileText');
    }

    return message;
  }

  private t(key: string): string {
    return this.transloco.translate(key);
  }
}